import cn from 'clsx';
import Link from 'next/link';
import {mutate} from 'swr';
import {useEffect, useState} from 'react';
import {useRouter} from 'next/router';
import {getFormData} from '@helpers/common/form.js';
import {CustomerLoginPageHeader} from '@components/header/HeaderFontStyling';
import {useIsMobile} from '@helpers/hooks/useWindow';
import {HStack, Spinner} from '@components/widgets/Common';
import {useLoginV2, useLogoutV2} from '@components/accounts/useLoginLogoutV2';
import {useDatabaseCartProviderV3} from '@components/cart/CartProvider';
import {useSession} from 'next-auth/react';

export const LoginForm = ({header = false, disableHeader = false}) => {
  const router = useRouter();
  const {data: session} = useSession();
  const isMobile = useIsMobile();
  const {cartItemsSimple} = useDatabaseCartProviderV3();
  const {loginV2} = useLoginV2();
  const {logoutV2} = useLogoutV2();
  const [isLoggingIn, setLoggingIn] = useState(false);
  const [email, setEmail] = useState('');
  const [emailFocus, setEmailFocus] = useState(false);
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [message, setMessage] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [isBanned, setBanned] = useState(false);
  // console.log('[LoginPage][cartLineItems][Before Login] cartItemsSimple:', {cartItemsSimple});

  const isLoggedIn = session && !!session?.user?.email && !isLoggingIn;

  const callbackUrl = router?.query?.callbackUrl ? router?.query?.callbackUrl : '/account';

  useEffect(() => {
    if (isLoggedIn && !isLoggingIn && router?.isReady) {
      router.push('/account', undefined, {shallow: false});
    }
  }, [isLoggedIn, isLoggingIn, router]);

  const handleSelect = (e) => {
    // e.target.style.backgroundColor = '#FFFF99';
  };

  const handleBlur = (e) => {
    setEmailFocus(false);
    // e.target.style.backgroundColor = '';
  };

  const fetchToken = async (e) => {
    e.preventDefault();
    const formData = getFormData(e.target);
    try {
      setLoggingIn(true);
      setMessage('');
      setDisabled(true);

      const body = {
        email: formData.email,
        password: formData.password,
        lineItems: cartItemsSimple,
        callbackUrl
      };
      console.log('[Login][Body]:', body);
      const response = await loginV2(body);
      console.log("[LoginForm][Response]:", response);

      const loginSuccess = response?.success === true;
      if (!loginSuccess) {
        if (response?.isBanned) {
          setLoggingIn(false);
          setBanned(true);
          setLoggingIn(false);
          setDisabled(false);
          await logoutV2();
          return;
        }

        setMessage(response?.reason ?? 'Error logging in');
        setDisabled(false);
        setLoggingIn(false);
        return;
      }

      if (loginSuccess) {
        await mutate(`/api/cartv2`);
        return await router.push(response?.redirect ?? '/account');
      }
      // NO REDIRECT IF LOGIN FROM /dealers or /become-a-dealer PAGE
    } catch (e) {
      console.error(e?.message);
      setMessage(e?.message);
      setLoggingIn(false);
      setDisabled(false);
    }
  };

  let headerText;
  if (!header) {
    if (!disableHeader) {
      headerText = `Customer Login`;
    }
    if (disableHeader === true) {
      headerText = ``;
    }
  }

  if (header) {
    headerText = header;
  }

  return (
    <>
      <div className="max-w-[1120px] m-auto">
        <CustomerLoginPageHeader isMobile={isMobile} title={headerText} />
        <div className="flex justify-center mt-1 mb-4">
          <div className="py-4 px-6 mt-4" style={{width: 400}}>

          {!isLoggedIn && (
              <form onSubmit={fetchToken}>
                <div>
                  <label htmlFor="email" className="block text-gray-800 font-bold mb-1">
                    Email:
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    autoComplete="username"
                    autoCorrect="off"
                    autoFocus={true}
                    required
                    placeholder="email@address.com"
                    value={email}
                    className="py-2 pl-2 outline-none ring-0"
                    onFocus={() => setEmailFocus(true)}
                    onBlur={(e) => {
                      handleBlur(e);
                    }}
                    // onSelect={handleSelect}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    style={{
                      fontSize: '16px',
                      width: '343px',
                      height: '44px',
                      backgroundColor: emailFocus ? 'transparent' : 'transparent',
                      border: emailFocus ? 'solid 2.5px' : 'solid 1px',
                      borderColor: emailFocus ? 'rgb(89,89,89)' : 'rgb(117,117,117)'
                    }}
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="password" className="block text-gray-800 font-bold mt-3 mb-1">
                    Password:
                  </label>
                  <input
                    type="password"
                    name="password"
                    id="password"
                    autoComplete="current-password"
                    autoFocus={false}
                    placeholder="password"
                    required
                    defaultValue=""
                    className="py-2 pl-2 dark:bg-white dark:text-black outline-none ring-0"
                    onFocus={() => setPasswordFocus(true)}
                    onBlur={(e) => {
                      handleBlur(e);
                    }}
                    onSelect={handleSelect}
                    style={{
                      fontSize: '16px',
                      width: '343px',
                      height: '44px',
                      backgroundColor: 'white',
                      border: passwordFocus ? 'solid 2.5px' : 'solid 1px',
                      borderColor: passwordFocus ? 'rgb(89,89,89)' : 'rgb(117,117,117)'
                    }}
                  />

                  <div className="text-left mt-0.5">
                    <Link
                      href="/account/password-forgot"
                      className="hover:underline mt-1 inline-block hover:text-indigo-600"
                      style={{
                        color: 'rgb(14, 112, 190)',
                        fontSize: '15px'
                      }}>
                      Forgot Password?
                    </Link>
                  </div>
                </div>

                <div className="flex justify-center items-center">
                  <button
                    type="submit"
                    className="cursor-pointer py-2 px-4 block text-white text-center
                     bg-neutral-700 hover:bg-neutral-800 rounded-3 text-lg font-bold leading-6"
                    disabled={disabled}>
                    <HStack gap={3}>
                      <div className="w-10">{isLoggingIn && <Spinner />}</div>
                      <div>Login</div>
                      <div className="w-10" />
                    </HStack>
                  </button>
                </div>

                {message && <div className="text-rose-500 p-3">{message}</div>}

                {isBanned && <BannedUserError />}
              </form>
            )}

            <div className="w-full flex justify-center items-center mb-3">
              <div
                style={{
                  marginTop: '7px'
                }}>
                New Customer?&nbsp;
                <Link
                  href="/account/register"
                  className="text-sm font-oswald text-gray-800 hover:underline hover:text-indigo-600">
                  Create Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/*</CenterStage>*/}
    </>
  );
};

export function CenterStage({children, className}) {
  return (
    <div className="relative mb-12">
      <div className={cn('h-screen flex flex-col items-center justify-start', className)}>
        {children}
      </div>
    </div>
  );
}

export function BannedUserError() {
  return (
    <div>
      <div className="border mt-2 rounded-sm p-1 mx-3" style={{background: 'rgb(252,166,165)'}}>
        <div className="text-black text-center">
          <p className="mb-7">
            Your account has been <strong>BANNED</strong>
          </p>
          <p>
            Due to highly suspect activity on our website, your account has been banned and any
            attempts to complete a purchase will be blocked. If you believe this ban is an error,
            please email our support.
          </p>
        </div>
      </div>
    </div>
  );
}
